import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATUSES = [
  { id: "novo", label: "Novo" },
  { id: "confirmado", label: "Confirmado" },
  { id: "em_preparacao", label: "Em preparação" },
  { id: "pronto", label: "Pronto" },
  { id: "entregue", label: "Entregue" },
  { id: "cancelado", label: "Cancelado" },
];

const STATUS_COLORS = {
  novo: "bg-rose/30 text-cocoa-dark",
  confirmado: "bg-mint/30 text-cocoa-dark",
  em_preparacao: "bg-amber-200 text-cocoa-dark",
  pronto: "bg-mint/40 text-cocoa-dark",
  entregue: "bg-cocoa-dark text-cream",
  cancelado: "bg-cocoa/15 text-cocoa-soft",
};

const PAY_LABELS = {
  aguarda_sinal: "Aguarda sinal",
  sinal_pago: "Sinal pago (50%)",
  totalmente_pago: "Totalmente pago",
  falhou: "Falhou",
};

export default function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status });
      toast.success("Estado atualizado");
      load();
    } catch (e) {
      toast.error("Erro");
    }
  };

  const markFullyPaid = async (id) => {
    try {
      await api.patch(`/admin/orders/${id}/payment`, { payment_status: "totalmente_pago" });
      toast.success("Marcado como totalmente pago");
      load();
    } catch (e) {
      toast.error("Erro");
    }
  };

  return (
    <div data-testid="admin-orders" className="p-10">
      <p className="text-xs uppercase tracking-widest text-rose-accent">Pedidos</p>
      <h1 className="font-heading text-5xl text-cocoa-dark mt-2">Encomendas</h1>

      <div className="mt-8 space-y-3">
        {orders.length === 0 ? (
          <p className="text-cocoa-soft">Sem encomendas ainda.</p>
        ) : (
          orders.map((o) => (
            <div key={o.id} data-testid={`order-row-${o.id}`} className="bg-cream rounded-2xl border border-cocoa/10 overflow-hidden">
              <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="w-full flex items-center justify-between p-5 text-left hover:bg-cream-alt/40">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-heading text-xl text-cocoa-dark">{o.customer_name}</span>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${STATUS_COLORS[o.status]}`}>
                      {STATUSES.find((s) => s.id === o.status)?.label}
                    </span>
                    {o.payment_status && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-mint/30 text-cocoa-dark">{PAY_LABELS[o.payment_status] || o.payment_status}</span>
                    )}
                  </div>
                  <p className="text-xs text-cocoa-soft mt-1">{new Date(o.created_at).toLocaleString("pt-PT")} · {o.items.length} item(s) · Levantamento {o.pickup_date} {o.pickup_time}</p>
                </div>
                <div className="text-right">
                  <div className="font-heading text-2xl text-cocoa-dark">€{o.total.toFixed(2)}</div>
                  <div className="text-xs text-cocoa-soft mt-1">{o.id.slice(0, 8)}</div>
                </div>
              </button>

              {expanded === o.id && (
                <div className="border-t border-cocoa/10 p-5 bg-cream-deep/40">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-xs uppercase tracking-widest text-cocoa-soft mb-2">Contacto & Levantamento</h4>
                      <p className="text-sm text-cocoa-dark">{o.customer_email}</p>
                      <p className="text-sm text-cocoa-dark">{o.customer_phone}</p>
                      <p className="text-sm text-cocoa-dark mt-2">{o.delivery_address || "—"}</p>
                      <p className="text-xs text-cocoa-soft mt-3 italic">{o.notes || "Sem observações"}</p>
                      <p className="text-xs text-cocoa-soft mt-3">MB Way: {o.mbway_phone}</p>
                      <div className="mt-3 bg-rose/10 rounded-lg px-3 py-2 text-xs">
                        <p className="text-cocoa-dark"><strong>Levantamento:</strong> {o.pickup_date} às {o.pickup_time}</p>
                        <p className="text-cocoa-dark mt-1"><strong>Sinal:</strong> €{(o.deposit_amount||0).toFixed(2)} · <strong>Falta:</strong> €{(o.remaining_amount||0).toFixed(2)}</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs uppercase tracking-widest text-cocoa-soft mb-2">Itens</h4>
                      <ul className="space-y-2 text-sm">
                        {o.items.map((it, idx) => (
                          <li key={`${o.id}-${it.product_id}-${it.variant_id}-${idx}`} className="flex flex-col gap-0.5 pb-2 border-b border-cocoa/5 last:border-0">
                            <div className="flex justify-between gap-2">
                              <span className="text-cocoa-dark">{it.name} <span className="text-cocoa-soft text-xs">— {it.variant_label} × {it.quantity}</span></span>
                              <span className="text-cocoa-dark">€{(it.price * it.quantity).toFixed(2)}</span>
                            </div>
                            {it.selected_flavors && Object.entries(it.selected_flavors).map(([k, f]) => f.length > 0 && (
                              <span key={k} className="text-[11px] text-cocoa-soft pl-1"><span className="capitalize font-medium">{k}:</span> {f.join(", ")}</span>
                            ))}
                            {it.customization && (it.customization.theme || it.customization.colors) && (
                              <div className="text-[11px] bg-rose/15 rounded px-2 py-1 mt-1 inline-block">
                                {it.customization.theme && <span className="text-cocoa-dark"><strong>Tema:</strong> {it.customization.theme}</span>}
                                {it.customization.colors && <span className="text-cocoa-dark ml-2"><strong>Cores:</strong> {it.customization.colors}</span>}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-5">
                    <h4 className="text-xs uppercase tracking-widest text-cocoa-soft mb-2">Atualizar estado</h4>
                    <div className="flex flex-wrap gap-2">
                      {STATUSES.map((s) => (
                        <button
                          key={s.id}
                          data-testid={`status-${o.id}-${s.id}`}
                          onClick={() => update(o.id, s.id)}
                          className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                            o.status === s.id ? "bg-cocoa-dark text-cream border-cocoa-dark" : "bg-cream text-cocoa-soft border-cocoa/15 hover:border-rose"
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                      {o.payment_status !== "totalmente_pago" && (
                        <button
                          data-testid={`mark-paid-${o.id}`}
                          onClick={() => markFullyPaid(o.id)}
                          className="text-xs uppercase tracking-widest px-3 py-1.5 rounded-full bg-mint text-cocoa-dark border border-mint hover:bg-mint/80 transition-colors"
                        >
                          Marcar pago no levantamento
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
